import { cloneElement, forwardRef, useEffect, useRef, useState } from 'react';
import { mergeRefs } from 'react-merge-refs';
import type { IsomorphicTooltipProps } from '../Tooltip/IsomorphicTooltip';
import { IsomorphicTooltip } from '../Tooltip/IsomorphicTooltip';

interface WrapWithTooltipIfOverflowingProps extends IsomorphicTooltipProps {
  readonly children: React.ReactElement;
}

export const WrapWithTooltipIfOverflowing = forwardRef<HTMLElement, WrapWithTooltipIfOverflowingProps>(
  ({ children, ...tooltip }, ref) => {
    const elementRef = useRef<HTMLDivElement>(null);
    const [shouldShowTooltip, setShouldShowTooltip] = useState(false);

    useEffect(() => {
      const checkOverflow = (): void => {
        /* istanbul ignore if -- @preserve - can't mock null ref */
        if (elementRef.current) {
          const { scrollWidth, clientWidth } = elementRef.current;
          setShouldShowTooltip(scrollWidth > clientWidth);
        }
      };

      checkOverflow();

      window.addEventListener('resize', checkOverflow);
      return () => window.removeEventListener('resize', checkOverflow);
    }, []);

    const childrenWithRef = cloneElement(children, { ref: mergeRefs([elementRef, ref]) });

    if (shouldShowTooltip) {
      return <IsomorphicTooltip {...tooltip}>{childrenWithRef}</IsomorphicTooltip>;
    }

    return childrenWithRef;
  },
);
